/**
 * Bounded diagnostics for the web VRS stage's remote <video> elements.
 *
 * Media-quality samples can show that a sender or receiver lost bitrate, but
 * they cannot distinguish an ordinary decoder/layer change from the DOM video
 * being reloaded or replaced. This tracker records that browser boundary while
 * keeping the hot path local, small and free of participant identifiers.
 */
export const MAX_REMOTE_VIDEO_DIAGNOSTICS_PER_PANE = 16;
/**
 * A weakening link changes decoded resolution every few seconds, so resize
 * records have their own small cap.
 */
export const MAX_REMOTE_VIDEO_RESIZES_PER_PANE = 4;
/**
 * Slots that only element reloads, replacements and media errors may use.
 * Playback and resize churn early in a call can never take them, so a reload
 * late in the call is still recorded.
 */
export const REMOTE_VIDEO_LIFECYCLE_RESERVE = 8;
const LIFECYCLE_PHASES = new Set([
    'abort',
    'emptied',
    'error',
    'loadstart'
]);
/**
 * Keeps browser media state numeric without spreading an uncertain value into
 * telemetry.
 *
 * @param {unknown} value - Browser media-element field.
 * @returns {number|undefined} The finite number, when one was supplied.
 */
function finiteNumber(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}
/**
 * Creates one tracker for one mounted room stage. `paneKey` is internal only;
 * telemetry receives the non-identifying pane role.
 *
 * @param {Function} report - Receives each bounded observation.
 * @returns {{ observe: Function }} A remote-video observer scoped to the mounted room stage.
 */
export function createRemoteVideoDiagnostics(report) {
    const panes = new Map();
    // Panes remember an element by number only, so a replaced <video> can be
    // collected while the room stays mounted.
    const elementIds = new WeakMap();
    let lastElementId = 0;
    const observe = (paneKey, paneRole, phase, element) => {
        if (typeof element !== 'object' || element === null) {
            return;
        }
        let elementId = elementIds.get(element);
        if (elementId === undefined) {
            lastElementId += 1;
            elementId = lastElementId;
            elementIds.set(element, elementId);
            element.addEventListener?.('resize', () => observe(paneKey, paneRole, 'resize', element));
        }
        let state = panes.get(paneKey);
        if (!state) {
            state = {
                elementMounts: 0,
                emitted: 0,
                loadStarts: 0,
                resizes: 0,
                routine: 0,
                suppressed: 0
            };
            panes.set(paneKey, state);
        }
        const replaced = state.elementId !== elementId;
        if (replaced) {
            state.elementId = elementId;
            state.elementMounts += 1;
            state.lastMetadataLoad = undefined;
            state.lastVideoHeight = undefined;
            state.lastVideoWidth = undefined;
            state.playback = undefined;
        }
        if (phase === 'loadstart') {
            state.loadStarts += 1;
            state.lastMetadataLoad = undefined;
            state.playback = undefined;
        }
        const videoWidth = finiteNumber(element.videoWidth);
        const videoHeight = finiteNumber(element.videoHeight);
        // Browsers may raise resize without an intrinsic-size change. Only an
        // actual decoded resolution transition is useful for this incident.
        if (phase === 'resize'
            && state.lastVideoWidth === videoWidth
            && state.lastVideoHeight === videoHeight) {
            return;
        }
        // A track attach can surface loadedmetadata more than once for the same
        // load. Keep one initial-size record per load.
        if (phase === 'loadedmetadata' && state.lastMetadataLoad === state.loadStarts
            && state.lastVideoWidth === videoWidth && state.lastVideoHeight === videoHeight) {
            return;
        }
        if (phase === 'playing') {
            if (state.playback === 'playing') {
                return;
            }
            state.playback = 'playing';
        }
        else if (phase === 'waiting' || phase === 'stalled' || phase === 'emptied') {
            if (state.playback === phase) {
                return;
            }
            state.playback = phase;
        }
        const lifecycle = replaced || LIFECYCLE_PHASES.has(phase);
        const routineLimit = MAX_REMOTE_VIDEO_DIAGNOSTICS_PER_PANE - REMOTE_VIDEO_LIFECYCLE_RESERVE;
        if (state.emitted >= MAX_REMOTE_VIDEO_DIAGNOSTICS_PER_PANE
            || (!lifecycle && state.routine >= routineLimit)
            || (phase === 'resize' && state.resizes >= MAX_REMOTE_VIDEO_RESIZES_PER_PANE)) {
            state.suppressed += 1;
            return;
        }
        state.emitted += 1;
        if (!lifecycle) {
            state.routine += 1;
        }
        if (phase === 'resize') {
            state.resizes += 1;
        }
        state.lastVideoWidth = videoWidth;
        state.lastVideoHeight = videoHeight;
        if (phase === 'loadedmetadata') {
            state.lastMetadataLoad = state.loadStarts;
        }
        report({
            elementMounts: state.elementMounts,
            eventSequence: state.emitted,
            loadStarts: state.loadStarts,
            networkState: finiteNumber(element.networkState),
            paneRole,
            phase,
            readyState: finiteNumber(element.readyState),
            suppressed: state.suppressed,
            videoHeight,
            videoWidth
        });
    };
    return { observe };
}
